import { ReportExportButtons } from '../components/ReportExportButtons'
import { TreasuryWaterfallPanel } from '../components/TreasuryWaterfallPanel'
import { TreasuryOutlookChart } from '../components/TreasuryOutlookChart'
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { type LoginResponse } from '../api/auth'
import { costCategoriesApi, type CostCategory } from '../api/costs'
import { suppliersApi, type SupplierOption } from '../api/finance-ext'
import { useFeatureFlag } from '../lib/featureFlags'
import { TREASURY_HORIZONS, treasuryApi, type Treasury, type TreasuryOutlook } from '../api/treasury'
import {
  CALENDAR_BUCKETS,
  CALENDAR_HORIZONS,
  SCHEDULED_FREQUENCIES,
  SCHEDULED_KINDS,
  categoryBucketApi,
  paymentCalendarApi,
  scheduledPaymentsApi,
  type CalendarDirection,
  type CalendarItem,
  type PaymentCalendar,
  type ScheduledPayment,
  type ScheduledPaymentBody,
} from '../api/paymentCalendar'
import {
  FormGrid,
  Button,
  ButtonGroup,
  Callout,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  DataTable,
  DataTableEmptyRow,
  EmptyState,
  FilterBar,
  IconButton,
  Page,
  PageHeader,
  Select,
  StatCard as UiStatCard,
  StatGrid,
  Tabs,
  TextField,
  useConfirm,
} from '../components/ui'

/**
 * F-085 (TASK-0339, REQ-176-001 / REQ-167-002 / REQ-126-001 / REQ-177-004) — scadenziario unico dei
 * pagamenti e degli incassi, scadenze inserite a mano e gruppi per categoria.
 *
 * <b>Nessun calcolo qui.</b> Voci, totali per gruppo e per settimana, scadute e riepilogo dei prossimi
 * 30 giorni arrivano dal server. Nessun saldo prospettico, runway o previsione: solo le scadenze note.
 * Le imposte sono scadenze inserite a mano (nessun calcolo fiscale). Gli importi sono visibili solo a
 * chi ha accesso Amministrazione (il server lo impone); il Commercialista legge soltanto.
 */

interface Props {
  auth: LoginResponse
}

type Tab = 'calendar' | 'manual' | 'groups' | 'treasury'

const TABS: { id: Tab; label: string }[] = [
  { id: 'calendar', label: 'Scadenziario' },
  { id: 'manual', label: 'Scadenze manuali' },
  { id: 'groups', label: 'Gruppi per categoria' },
  { id: 'treasury', label: 'Tesoreria (da conti)' },
]

/** Solo un suggerimento in schermata: nessun abbinamento viene applicato da solo. */
const SUGGESTED_BUCKET: Record<string, string> = {
  affitto: 'Affitti',
  personale: 'Personale',
  collaboratori: 'Personale',
  software: 'Software',
  imposte: 'Imposte',
  assicurazioni: 'Assicurazioni',
  provvigioni: 'Provvigioni',
}

const SOURCE_LABEL: Record<string, string> = {
  expense: 'Spesa',
  invoice: 'Fattura',
  payment_batch: 'Distinta provvigioni',
  scheduled_payment: 'Scadenza manuale',
  recurring_expense: 'Ricorrente (prevista)',
}

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(s?: string | null): string {
  if (!s) return '—'
  return new Date(s + (s.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('it-IT')
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export function PaymentCalendarPage({ auth }: Props) {
  const writer = auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
  const [tab, setTab] = useState<Tab>('calendar')
  // F-145: la scheda Tesoreria (letta da conti) compare solo con il flag acceso; nessuna riga seminata = spenta.
  const treasuryOn = useFeatureFlag('finanza.tesoreria_conti')
  const tabs = TABS.filter(t => t.id !== 'treasury' || treasuryOn)

  return (
    <Page>
      <PageHeader title="Scadenziario" icon="event_upcoming" actions={<ReportExportButtons title="Scadenziario" />} />
      {!writer && (
        <Callout tone="info">Sola lettura: le modifiche richiedono il ruolo Amministrazione.</Callout>
      )}

      <Tabs
        aria-label="Sezioni dello scadenziario"
        value={tab}
        onChange={id => setTab(id as Tab)}
        items={tabs.map(t => ({ id: t.id, label: t.label }))}
      />

      {tab === 'calendar' && <CalendarTab />}
      {tab === 'manual' && <ManualTab writer={writer} />}
      {tab === 'groups' && <GroupsTab writer={writer} />}
      {tab === 'treasury' && treasuryOn && <TreasuryTab />}
    </Page>
  )
}

/* ── Scadenziario ────────────────────────────────────────────────────────────────────────────── */

function StatCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <UiStatCard label={label} value={value} supporting={note} />
  )
}

function CalendarTab() {
  const [days, setDays] = useState<number>(30)
  const [bucket, setBucket] = useState('')
  const [direction, setDirection] = useState('')
  const [data, setData] = useState<PaymentCalendar | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setError('')
    paymentCalendarApi.get({ days, bucket: bucket || undefined, direction: direction || undefined })
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) { setData(null); setError(errMsg(e, 'Errore durante il caricamento dello scadenziario')) } })
    return () => { cancelled = true }
  }, [days, bucket, direction])

  const overdueItems = useMemo(() => (data?.items ?? []).filter(i => i.overdue), [data])
  const weeks = useMemo(() => {
    const byWeek = new Map<string, CalendarItem[]>()
    for (const i of (data?.items ?? []).filter(x => !x.overdue)) {
      const key = weekStartOf(i.date)
      byWeek.set(key, [...(byWeek.get(key) ?? []), i])
    }
    return [...byWeek.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [data])

  return (
    <>
      <FilterBar>
        <span className="md-typescale-body-small text-muted">Prossimi</span>
        <ButtonGroup
          aria-label="Prossimi giorni"
          size="sm"
          options={CALENDAR_HORIZONS.map(d => ({ value: String(d), label: `${d} giorni` }))}
          value={String(days)}
          onChange={v => setDays(Number(v))}
        />
        <Select label="Gruppo" labelHidden value={bucket} onChange={e => setBucket(e.target.value)} aria-label="Gruppo">
          <option value="">Tutti i gruppi</option>
          {CALENDAR_BUCKETS.map(b => <option key={b} value={b}>{b}</option>)}
        </Select>
        <Select label="Direzione" labelHidden value={direction} onChange={e => setDirection(e.target.value)} aria-label="Direzione">
          <option value="">Entrate e uscite</option>
          <option value="Entrata">Solo entrate</option>
          <option value="Uscita">Solo uscite</option>
        </Select>
      </FilterBar>
      {error && <Callout tone="error">{error}</Callout>}
      {data && (
        <>
          <StatGrid>
            <StatCard label="Entrate a 30 giorni" value={formatEur(data.next30.entrate)} note={`${data.next30.count} voci in totale (entrate e uscite)`} />
            <StatCard label="Uscite a 30 giorni" value={formatEur(data.next30.uscite)} note="Solo scadenze note: nessuna previsione" />
            <StatCard label="Scadute non pagate" value={formatEur(data.overdue.uscite)} note={`Incassi scaduti: ${formatEur(data.overdue.entrate)}`} />
          </StatGrid>

          {overdueItems.length > 0 && <ItemsCard title="Scadute" items={overdueItems} />}

          {weeks.length === 0 && overdueItems.length === 0 && (
            <EmptyState icon="event_busy" title="Nessuna scadenza nel periodo scelto." />
          )}
          {weeks.map(([start, items]) => {
            const total = data.totalsByWeek.find(w => w.weekStart === start)
            return (
              <ItemsCard
                key={start}
                title={`Settimana dal ${formatDate(start)}`}
                items={items}
                summary={total ? `Entrate ${formatEur(total.entrate)} · Uscite ${formatEur(total.uscite)}` : undefined}
              />
            )
          })}

          <Card padding="none">
            <CardHeader title={`Totali per gruppo (fino al ${formatDate(data.until)})`} />
              <DataTable variant="plain">
                <thead><tr><th>Gruppo</th><th className="text-end">Voci</th><th className="text-end">Entrate</th><th className="text-end">Uscite</th></tr></thead>
                <tbody>
                  {data.totalsByBucket.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessun totale" />}
                  {data.totalsByBucket.map(b => (
                    <tr key={b.bucket}>
                      <td>{b.bucket}</td>
                      <td className="text-end md-num">{b.count}</td>
                      <td className="text-end text-nowrap md-num">{formatEur(b.entrate)}</td>
                      <td className="text-end text-nowrap md-num">{formatEur(b.uscite)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>
        </>
      )}
    </>
  )
}

/** Il lunedì della settimana di una data ISO (stessa regola del server). */
function weekStartOf(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  const offset = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - offset)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function ItemsCard({ title, items, summary }: { title: string; items: CalendarItem[]; summary?: string | undefined }) {
  return (
    <Card padding="none">
      <CardHeader title={title} subtitle={summary} />
        <DataTable variant="plain">
          <thead><tr><th>Data</th><th>Gruppo</th><th>Descrizione</th><th>Fonte</th><th>Stato</th><th className="text-end">Importo</th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={`${i.source.type}-${i.source.id}-${i.date}`} style={i.overdue ? { background: 'hsl(var(--md-error-container))' } : undefined}>
                <td className="text-nowrap">{formatDate(i.date)}</td>
                <td>{i.bucket}</td>
                <td>{i.description}</td>
                <td className="md-typescale-body-small text-muted">{SOURCE_LABEL[i.source.type] ?? i.source.type}</td>
                <td>{i.status}</td>
                <td className={`text-end text-nowrap md-num ${i.direction === 'Entrata' ? 'text-success' : ''}`}>
                  {i.direction === 'Entrata' ? '+' : '−'} {formatEur(i.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
    </Card>
  )
}

/* ── Scadenze manuali ────────────────────────────────────────────────────────────────────────── */

interface FormState {
  description: string
  kind: string
  direction: CalendarDirection
  amount: string
  dueDate: string
  frequency: string
  intervalMonths: string
  endDate: string
  supplierId: string
  expenseCategoryId: string
  counterpartyName: string
  notes: string
}

const EMPTY_FORM: FormState = {
  description: '', kind: 'Imposta', direction: 'Uscita', amount: '', dueDate: todayIso(), frequency: '',
  intervalMonths: '', endDate: '', supplierId: '', expenseCategoryId: '', counterpartyName: '', notes: '',
}

function toBody(f: FormState): ScheduledPaymentBody {
  return {
    description: f.description.trim(),
    kind: f.kind,
    direction: f.direction,
    amount: Number(f.amount.replace(',', '.')),
    dueDate: f.dueDate,
    frequency: f.frequency || undefined,
    intervalMonths: f.frequency === 'Personalizzata' && f.intervalMonths ? Number(f.intervalMonths) : undefined,
    endDate: f.frequency && f.endDate ? f.endDate : undefined,
    supplierId: f.supplierId ? Number(f.supplierId) : undefined,
    expenseCategoryId: f.expenseCategoryId ? Number(f.expenseCategoryId) : undefined,
    counterpartyName: f.counterpartyName.trim() || undefined,
    notes: f.notes.trim() || undefined,
  }
}

function ManualTab({ writer }: { writer: boolean }) {
  const confirm = useConfirm()
  const [items, setItems] = useState<ScheduledPayment[]>([])
  const [status, setStatus] = useState('DaPagare')
  const [categories, setCategories] = useState<CostCategory[]>([])
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  const [form, setForm] = useState<FormState | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const r = await scheduledPaymentsApi.list(status)
      setItems(r.items)
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle scadenze'))
    }
  }, [status])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    costCategoriesApi.list().then(setCategories).catch(() => setCategories([]))
    suppliersApi.list().then(setSuppliers).catch(() => setSuppliers([]))
  }, [])

  function startNew() {
    setEditingId(null)
    setForm({ ...EMPTY_FORM, dueDate: todayIso() })
  }

  function startEdit(p: ScheduledPayment) {
    setEditingId(p.id)
    setForm({
      description: p.description, kind: p.kind, direction: p.direction, amount: String(p.amount),
      dueDate: p.dueDate, frequency: p.frequency ?? '', intervalMonths: p.intervalMonths ? String(p.intervalMonths) : '',
      endDate: p.endDate ?? '', supplierId: p.supplierId ? String(p.supplierId) : '',
      expenseCategoryId: p.expenseCategoryId ? String(p.expenseCategoryId) : '',
      counterpartyName: p.counterpartyName ?? '', notes: p.notes ?? '',
    })
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!form) return
    setBusy(true)
    setError('')
    try {
      const body = toBody(form)
      if (editingId) await scheduledPaymentsApi.update(editingId, body)
      else await scheduledPaymentsApi.create(body)
      setForm(null)
      setEditingId(null)
      await load()
    } catch (err) {
      setError(errMsg(err, 'Salvataggio non riuscito'))
    } finally {
      setBusy(false)
    }
  }

  async function act(fn: () => Promise<unknown>, fallback: string) {
    setBusy(true)
    setError('')
    try {
      await fn()
      await load()
    } catch (err) {
      setError(errMsg(err, fallback))
    } finally {
      setBusy(false)
    }
  }

  const set = (patch: Partial<FormState>) => setForm(f => (f ? { ...f, ...patch } : f))

  return (
    <>
      <FilterBar>
        <Select label="Stato" value={status} onChange={e => setStatus(e.target.value)} aria-label="Stato">
          <option value="DaPagare">Da pagare</option>
          <option value="Pagata">Pagate</option>
          <option value="Annullata">Annullate</option>
        </Select>
      </FilterBar>
      <div className="d-flex gap-3 align-items-center flex-wrap mb-3">
        {writer && <Button variant="filled" onClick={startNew}>Nuova scadenza</Button>}
        <span className="md-typescale-body-small text-muted">Imposte e altre scadenze inserite a mano: nessun calcolo fiscale.</span>
      </div>
      {error && <Callout tone="error">{error}</Callout>}

      {writer && form && (
        <form onSubmit={submit}>
          <Card>
            <CardHeader title={editingId ? 'Modifica scadenza' : 'Nuova scadenza'} />
            <CardContent>
              <FormGrid columns={4}>
                <div className="md-span-2">
                  <TextField label="Descrizione" maxLength={200} required value={form.description} onChange={e => set({ description: e.target.value })} />
                </div>
                <div>
                  <Select label="Tipo" value={form.kind} onChange={e => set({ kind: e.target.value })}>
                    {SCHEDULED_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                  </Select>
                </div>
                <div>
                  <Select label="Direzione" value={form.direction} onChange={e => set({ direction: e.target.value as CalendarDirection })}>
                    <option value="Uscita">Uscita (da pagare)</option>
                    <option value="Entrata">Entrata (da incassare)</option>
                  </Select>
                </div>
                <div>
                  <TextField label="Importo (€)" inputMode="decimal" required value={form.amount} onChange={e => set({ amount: e.target.value })} />
                </div>
                <div>
                  <TextField label={form.frequency ? 'Prima scadenza' : 'Scadenza'} type="date" required value={form.dueDate} onChange={e => set({ dueDate: e.target.value })} />
                </div>
                <div>
                  <Select label="Ricorrenza" value={form.frequency} onChange={e => set({ frequency: e.target.value })}>
                    <option value="">Una sola volta</option>
                    {SCHEDULED_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </Select>
                </div>
                {form.frequency === 'Personalizzata' && (
                  <div>
                    <TextField label="Ogni quanti mesi (1-60)" type="number" min={1} max={60} required value={form.intervalMonths} onChange={e => set({ intervalMonths: e.target.value })} />
                  </div>
                )}
                {form.frequency && (
                  <div>
                    <TextField label="Fine (facoltativa)" type="date" value={form.endDate} onChange={e => set({ endDate: e.target.value })} />
                  </div>
                )}
                <div>
                  <Select label="Fornitore" value={form.supplierId} onChange={e => set({ supplierId: e.target.value })}>
                    <option value="">—</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
                  </Select>
                </div>
                <div>
                  <Select label="Categoria di spesa" value={form.expenseCategoryId} onChange={e => set({ expenseCategoryId: e.target.value })}>
                    <option value="">—</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
                <div>
                  <TextField label="Controparte" maxLength={200} value={form.counterpartyName} onChange={e => set({ counterpartyName: e.target.value })} />
                </div>
                <div className="md-span-full">
                  <TextField label="Note" multiline rows={2} maxLength={2000} value={form.notes} onChange={e => set({ notes: e.target.value })} />
                </div>
              </FormGrid>
            </CardContent>
            <CardActions align="start">
              <Button variant="text" onClick={() => { setForm(null); setEditingId(null) }}>Annulla</Button>
              <Button type="submit" variant="tonal" disabled={busy}>Salva</Button>
            </CardActions>
          </Card>
        </form>
      )}

      <Card padding="none">
          <DataTable variant="plain">
            <thead><tr><th>Scadenza</th><th>Descrizione</th><th>Tipo</th><th>Ricorrenza</th><th>Direzione</th><th className="text-end">Importo</th><th /></tr></thead>
            <tbody>
              {items.length === 0 && <DataTableEmptyRow colSpan={7} title="Nessuna scadenza" />}
              {items.map(p => (
                <tr key={p.id}>
                  <td className="text-nowrap">{formatDate(p.dueDate)}</td>
                  <td>{p.description}</td>
                  <td>{p.kind}</td>
                  <td>{p.frequency ? `${p.frequency}${p.endDate ? ` fino al ${formatDate(p.endDate)}` : ''}` : 'Una volta'}</td>
                  <td>{p.direction}</td>
                  <td className="text-end text-nowrap md-num">{formatEur(p.amount)}</td>
                  <td className="text-end text-nowrap">
                    {writer && p.status === 'DaPagare' && (
                      <>
                        <IconButton icon="task_alt" size="sm" disabled={busy}
                          aria-label={`Segna ${p.direction === 'Entrata' ? 'incassata' : 'pagata'}`}
                          onClick={() => void act(() => scheduledPaymentsApi.markPaid(p.id), 'Operazione non riuscita')} />
                        <IconButton icon="edit" size="sm" aria-label="Modifica" disabled={busy} onClick={() => startEdit(p)} />
                        <IconButton icon="cancel" size="sm" aria-label="Annulla scadenza" disabled={busy}
                          onClick={async () => {
                            if (!(await confirm({ title: 'Annulla scadenza', message: 'Annullare questa scadenza?', confirmLabel: 'Annulla scadenza', cancelLabel: 'Indietro', destructive: true }))) return
                            void act(() => scheduledPaymentsApi.cancel(p.id), 'Annullamento non riuscito')
                          }} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
    </>
  )
}

/* ── Gruppi per categoria ────────────────────────────────────────────────────────────────────── */

function GroupsTab({ writer }: { writer: boolean }) {
  const [categories, setCategories] = useState<CostCategory[]>([])
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      setCategories(await costCategoriesApi.list(false))
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle categorie'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function change(id: number, bucket: string | null) {
    setBusyId(id)
    setError('')
    try {
      await categoryBucketApi.set(id, bucket)
      await load()
    } catch (e) {
      setError(errMsg(e, 'Salvataggio non riuscito'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <Callout tone="info">
        Il gruppo decide dove compaiono nello scadenziario le spese di una categoria. Senza gruppo una spesa va in
        «Fornitori» se ha il fornitore, altrimenti in «Altro». Il suggerimento dal nome non viene applicato da solo.
      </Callout>
      {error && <Callout tone="error">{error}</Callout>}
      <Card padding="none">
          <DataTable variant="plain">
            <thead><tr><th>Categoria</th><th>Gruppo</th><th>Suggerito dal nome</th></tr></thead>
            <tbody>
              {categories.map(c => {
                const suggestion = SUGGESTED_BUCKET[c.name.trim().toLowerCase()]
                return (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td className="md-u-minw-200">
                      <Select
                        label={`Gruppo di ${c.name}`}
                        labelHidden
                        value={c.calendarBucket ?? ''}
                        disabled={!writer || busyId === c.id}
                        onChange={e => void change(c.id, e.target.value || null)}
                        aria-label={`Gruppo di ${c.name}`}
                      >
                        <option value="">Nessuno</option>
                        {CALENDAR_BUCKETS.map(b => <option key={b} value={b}>{b}</option>)}
                      </Select>
                    </td>
                    <td>
                      {suggestion && c.calendarBucket !== suggestion && (
                        <>
                          <span className="me-2">{suggestion}</span>
                          {writer && (
                            <Button size="sm" variant="text" disabled={busyId === c.id}
                              onClick={() => void change(c.id, suggestion)}>
                              Applica
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
      </Card>
    </>
  )
}

/* ── Tesoreria (da conti) ────────────────────────────────────────────────────────────────────── */

const TREASURY_REASON: Record<string, string> = {
  disabled: "L'integrazione con conti non è attiva.",
  not_configured: "L'integrazione con conti non è configurata.",
  timeout: 'Conti non ha risposto in tempo.',
  unreachable: 'Conti non è raggiungibile.',
  unauthorized: 'Conti ha rifiutato le credenziali del CRM.',
  invalid_response: 'Conti ha risposto con dati non leggibili.',
  error: 'Conti ha risposto con un errore.',
}

/**
 * F-145 (REQ-153-001, REQ-056S-011): saldi, liquidità, IVA e imposte residue, incassi e spese del mese e coda da
 * classificare arrivano da conti.equalstudio.it e si mostrano così come sono (il CRM non salva saldi). Banche, movimenti,
 * spese e riconciliazione si gestiscono in conti. La previsione è una proiezione deterministica, non statistica.
 */
function TreasuryTab() {
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<Treasury | null>(null)
  const [outlook, setOutlook] = useState<TreasuryOutlook | null>(null)
  const [error, setError] = useState('')
  const waterfallOn = useFeatureFlag('finanza.cascata_liquidita')
  const seriesOn = useFeatureFlag('finanza.serie_saldo')

  useEffect(() => {
    let cancelled = false
    setError('')
    treasuryApi.get()
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) { setData(null); setError(errMsg(e, 'Errore durante il caricamento della tesoreria')) } })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    treasuryApi.outlook(days)
      .then(d => { if (!cancelled) setOutlook(d) })
      .catch(() => { if (!cancelled) setOutlook(null) })
    return () => { cancelled = true }
  }, [days])

  const num = (n: number | null | undefined) => (n == null ? '—' : formatEur(n))

  return (
    <>
      {error && <Callout tone="error">{error}</Callout>}
      {data && !data.available && (
        <Callout tone="warning">
          {TREASURY_REASON[data.reason ?? ''] ?? data.message ?? 'Conti non è disponibile.'} Nessun dato di tesoreria da mostrare.
        </Callout>
      )}
      {data?.available && (
        <>
          <StatGrid>
            <StatCard label="Liquidità società" value={num(data.liquidity)} {...(data.asOf ? { note: `Letta da conti il ${new Date(data.asOf).toLocaleString("it-IT")}` } : {})} />
            <StatCard label="IVA residua" value={num(data.vatDue)} />
            <StatCard label="Imposte residue" value={num(data.taxesDue)} />
            <StatCard label="Incassi del mese" value={num(data.incomeMonth)} note="Imponibile" />
            <StatCard label="Spese del mese" value={num(data.expensesMonth)} note="Importo lordo" />
            <StatCard label="Da classificare" value={String(data.toClassify ?? 0)} note="Movimenti in conti" />
          </StatGrid>
          <Card padding="none">
            <CardHeader title="Conti" />
              <DataTable variant="plain">
                <thead><tr><th>Conto</th><th>Tipo</th><th className="text-end">Saldo</th></tr></thead>
                <tbody>
                  {data.accounts.map(a => (
                    <tr key={a.externalId}><td>{a.name}</td><td>{a.kind}</td><td className="text-end md-num">{formatEur(a.balance)}</td></tr>
                  ))}
                  {data.accounts.length === 0 && <DataTableEmptyRow colSpan={3} title="Nessun conto." />}
                </tbody>
              </DataTable>
          </Card>
        </>
      )}

      <FilterBar>
        <span className="md-typescale-body-small text-muted">Saldo prospettico a</span>
        <ButtonGroup
          aria-label="Saldo prospettico a"
          size="sm"
          options={TREASURY_HORIZONS.map(d => ({ value: String(d), label: `${d} giorni` }))}
          value={String(days)}
          onChange={v => setDays(Number(v))}
        />
      </FilterBar>
      {outlook && (
        <Card>
          <CardContent>
            {outlook.available ? (
              <>
                <div className="md-typescale-headline-small md-num">{num(outlook.projected)}</div>
                <div className="md-typescale-body-small text-muted">
                  Fino al {formatDate(outlook.until)}: liquidità {num(outlook.liquidity)} + entrate attese {num(outlook.expectedIn)}
                  {' '}− uscite attese {num(outlook.expectedOut)} − spese ricorrenti di conti {num(outlook.recurringOut)}.
                </div>
              </>
            ) : (
              <div className="text-muted">
                Saldo prospettico non disponibile: {TREASURY_REASON[outlook.reason ?? ''] ?? 'conti non risponde.'} Entrate attese {num(outlook.expectedIn)}, uscite attese {num(outlook.expectedOut)} (solo CRM).
              </div>
            )}
            <div className="md-typescale-body-small text-muted mt-1">Proiezione deterministica ({outlook.method}): non è una previsione statistica.</div>
          </CardContent>
        </Card>
      )}
      {waterfallOn && <TreasuryWaterfallPanel />}
      {seriesOn && <TreasuryOutlookChart />}
    </>
  )
}
